import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { calendarService } from '../services/calendarService';
import { useApp } from '../contexts/AppContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiPlus, FiTrash2, FiCopy, FiExternalLink, FiCheck } = FiIcons;

const CalendarEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addCalendar, updateCalendar } = useApp();
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const isEditing = !!id;

  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      description: '',
      slots: [
        {
          date: format(new Date(), 'yyyy-MM-dd'),
          time: '09:00',
          duration: 60,
          isAvailable: true
        }
      ],
      settings: {
        allowMultipleBookings: false,
        requireApproval: false,
        bufferTime: 0,
        maxBookingsPerSlot: 1,
        timezone: 'Europe/Berlin'
      }
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'slots'
  });

  useEffect(() => {
    if (isEditing) {
      loadCalendar();
    }
  }, [id, isEditing]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const calendarData = await calendarService.getCalendarById(id);
      setCalendar(calendarData);

      // Set form values
      setValue('title', calendarData.title);
      setValue('description', calendarData.description);
      setValue('slots', calendarData.slots || []);
      setValue('settings', calendarData.settings);

      // Set share URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/#/book/${calendarData.shareId}`;
      setShareUrl(url);
    } catch (error) {
      toast.error('Fehler beim Laden des Kalenders');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let result;
      if (isEditing) {
        result = await calendarService.updateCalendar(id, data);
        updateCalendar(result);
        toast.success('Kalender erfolgreich aktualisiert!');
      } else {
        result = await calendarService.createCalendar(data);
        addCalendar(result);
        
        // Set share URL for new calendar
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/#/book/${result.shareId}`;
        setShareUrl(url);
        
        // Show success state
        setShowSuccess(true);
        toast.success('Kalender erfolgreich erstellt!');
      }
    } catch (error) {
      toast.error(error.message || 'Fehler beim Speichern des Kalenders');
    } finally {
      setLoading(false);
    }
  };

  const addSlot = () => {
    const lastSlot = fields[fields.length - 1];
    const nextDate = lastSlot 
      ? format(addDays(new Date(lastSlot.date), 1), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');
    
    append({
      date: nextDate,
      time: '09:00',
      duration: 60,
      isAvailable: true
    });
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link kopiert!');
    } catch (error) {
      toast.error('Fehler beim Kopieren des Links');
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading && isEditing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Success view for new calendar
  if (showSuccess && shareUrl) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-success-100 dark:bg-success-900 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10 text-success-600 dark:text-success-400" />
            </motion.div>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Kalender erfolgreich erstellt!
            </h1>
            
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              Ihr Kalender ist jetzt bereit für Buchungen. Teilen Sie den Link mit Ihren Kunden.
            </p>

            {/* Share URL Card */}
            <Card className="p-6 mb-8 max-w-2xl mx-auto">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Ihr Buchungslink
              </h2>
              <div className="flex items-center space-x-4 mb-4">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <Button type="button" onClick={copyShareUrl} variant="outline">
                  <SafeIcon icon={FiCopy} className="w-4 h-4 mr-2" />
                  Kopieren
                </Button>
                <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="button" variant="ghost">
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </Button>
                </a>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Teilen Sie diesen Link mit Ihren Kunden, damit sie Termine buchen können.
              </p>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={goToDashboard}>
                Zum Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(shareUrl, '_blank')}
              >
                <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-2" />
                Buchungsseite ansehen
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Kalender bearbeiten' : 'Neuen Kalender erstellen'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {isEditing ? 'Bearbeiten Sie die Details Ihres Kalenders' : 'Erstellen Sie einen neuen Kalender mit flexiblen Zeitslots'}
          </p>
        </motion.div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Grundinformationen
              </h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Titel *
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.title ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                    placeholder="z.B. Beratungstermine"
                    {...register('title', { required: 'Titel ist erforderlich' })}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Beschreibung
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Beschreibung des Kalenders..."
                    {...register('description')}
                  />
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Time Slots */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Zeitslots
                </h2>
                <Button type="button" onClick={addSlot} size="sm">
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                  Slot hinzufügen
                </Button>
              </div>

              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div key={field.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Datum
                        </label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          {...register(`slots.${index}.date`, { required: true })}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Uhrzeit
                        </label>
                        <input
                          type="time"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          {...register(`slots.${index}.time`, { required: true })}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Dauer (Min.)
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          {...register(`slots.${index}.duration`, { required: true })}
                        >
                          <option value={15}>15 Min</option>
                          <option value={30}>30 Min</option>
                          <option value={45}>45 Min</option>
                          <option value={60}>1 Std</option>
                          <option value={90}>1,5 Std</option>
                          <option value={120}>2 Std</option>
                        </select>
                      </div>

                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Einstellungen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max. Buchungen pro Slot
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    {...register('settings.maxBookingsPerSlot')}
                  >
                    <option value={1}>1 Person</option>
                    <option value={2}>2 Personen</option>
                    <option value={5}>5 Personen</option>
                    <option value={10}>10 Personen</option>
                    <option value={99}>Unbegrenzt</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pufferzeit (Min.)
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    {...register('settings.bufferTime')}
                  >
                    <option value={0}>Keine</option>
                    <option value={5}>5 Min</option>
                    <option value={10}>10 Min</option>
                    <option value={15}>15 Min</option>
                    <option value={30}>30 Min</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        {...register('settings.requireApproval')}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Buchungen müssen bestätigt werden
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        {...register('settings.allowMultipleBookings')}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Mehrfachbuchungen erlauben
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Share URL (for existing calendars) */}
          {shareUrl && isEditing && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Freigabe-Link
                </h2>
                <div className="flex items-center space-x-4">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <Button type="button" onClick={copyShareUrl} variant="outline">
                    <SafeIcon icon={FiCopy} className="w-4 h-4 mr-2" />
                    Kopieren
                  </Button>
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                    <Button type="button" variant="ghost">
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex justify-end space-x-4"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Abbrechen
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Aktualisieren' : 'Kalender erstellen'}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

export default CalendarEditorPage;